import React, { createContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { initialFilterState } from './constants';
import { AppState } from '../../reducers';
import { IFilterState } from './types';
import { ProductCondition } from '../../types';
import { trackPriceListFilterUpdate } from './components/tracking';
import { saveFiltersAsUserSettings } from './components/price-list/offer-list-filters/utils';

const initialContextValue: IPriceListFilterContext = {
  selectedFilters: initialFilterState,
  selectedLegacyVariants: null,
  selectedSizeVariants: null,
  selectedCondition: null,
  changeSingleFilterSelection: () => undefined,
  changeMultipleFilterSelection: () => undefined,
  reloadWithFilters: () => undefined,
  clearAll: () => undefined,
};

export const PriceListFilterContext = createContext<IPriceListFilterContext>(
  initialContextValue
);

export const PriceListFilterContextContainer: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<unknown>
>> = props => {
  const [filters, setFilters] = useState<IFilterState>(initialFilterState);

  const selectedLegacyVariants = useSelector(
    (state: AppState) => state.areas.product.variants
  );
  const selectedSizeVariants = useSelector(
    (state: AppState) => state.areas.product.variantSizes
  );
  const selectedCondition = useSelector(
    (state: AppState) => state.areas.product.condition
  );

  const changeSingleFilterSelection = (
    name: string,
    val: boolean,
    routeDispatch: (action: any) => void
  ) => {
    const selectedFilters = {
      ...filters,
      [name]: val,
    };
    saveFiltersAsUserSettings(selectedFilters);
    trackPriceListFilterUpdate(name, val);
    reloadWithFilters(selectedFilters, routeDispatch);
  };

  const changeMultipleFilterSelection = (
    newFilters: IFilterState,
    routeDispatch: (action: any) => void
  ) => {
    saveFiltersAsUserSettings(newFilters);
    reloadWithFilters(newFilters, routeDispatch);
  };

  const reloadWithFilters = (
    newFilters: IFilterState,
    routeDispatch: (action: any) => void,
    resetEverythingElse?: boolean
  ) => {
    setFilters(newFilters);
    routeDispatch({
      type: 'PRICE_FILTER',
      options: {
        ...newFilters,
      },
      variants: resetEverythingElse === true ? null : selectedLegacyVariants,
      variantSizes: resetEverythingElse === true ? null : selectedSizeVariants,
      condition: resetEverythingElse === true ? 'new' : selectedCondition,
    });
  };

  const clearAll = (routeDispatch: (action: any) => void) => {
    saveFiltersAsUserSettings(initialFilterState);
    reloadWithFilters(initialFilterState, routeDispatch, true);
  };

  return (
    <PriceListFilterContext.Provider
      value={{
        selectedFilters: filters,
        selectedLegacyVariants,
        selectedSizeVariants,
        selectedCondition,
        changeSingleFilterSelection,
        changeMultipleFilterSelection,
        reloadWithFilters,
        clearAll,
      }}
    >
      {props.children}
    </PriceListFilterContext.Provider>
  );
};

interface IPriceListFilterContext {
  selectedFilters: IFilterState;
  selectedLegacyVariants: number[] | null;
  selectedSizeVariants: string[] | null;
  selectedCondition: ProductCondition | null;
  changeSingleFilterSelection: (
    name: string,
    val: boolean,
    routeDispatch: (action: any) => void
  ) => void;
  changeMultipleFilterSelection: (
    newFilters: IFilterState,
    routeDispatch: (action: any) => void
  ) => void;
  reloadWithFilters: (
    newFilters: IFilterState,
    routeDispatch: (action: any) => void,
    resetVariantsAndConditions?: boolean
  ) => void;
  clearAll: (routeDispatch: (action: any) => void) => void;
}
